import { useState } from "react";
import Badge from "Components/UI/Badge";
import Button from "Components/UI/Button";
import Input from "Components/UI/Input";
import DetailSection from "Components/PatentDesign/DetailSection";
import FileList from "Components/PatentDesign/FileList";
import { disabledStyle, pdStyles } from "Components/PatentDesign/styles";
import {
  closePatentDesignDocumentRequest,
  createPatentDesignDocumentRequest,
  downloadPatentDesignDocument,
} from "JS/API";
import usePatentDesignDownload from "utils/usePatentDesignDownload";
import {
  DESCRIPTION_MAX_BYTES,
  DOCUMENT_REQUEST_STATUS,
  DOCUMENT_REQUEST_STATUS_STYLE,
  ERROR_MESSAGE,
  TITLE_MAX_LENGTH,
  formatDateTime,
  isDocumentRequestable,
  normalizeNewlines,
  utf8ByteLength,
} from "utils/patentDesign";

const DOCUMENT_REQUEST_ERROR_MESSAGE = {
  invalidRequestStatus: "취소·거절된 신청에는 추가 서류를 요청할 수 없습니다.",
};

const CLOSE_ACTION = {
  requested: { label: "요청 철회", confirm: (title) => `'${title}' 요청을 철회할까요?` },
  submitted: {
    label: "확인 완료",
    confirm: (title) => `'${title}' 요청을 확인 완료로 닫을까요?`,
  },
};

const toFileItems = (files = []) =>
  files.map((file) => ({ id: file.id, name: file.original_file_name, size: file.file_size }));

const DocumentRequestSection = ({ detail, onReload, onAlert, onError }) => {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [closingId, setClosingId] = useState(null);
  const { isDownloading, download } = usePatentDesignDownload({ onAlert, onError });

  const requests = detail.document_requests || [];
  const canRequestDocument = isDocumentRequestable(detail.status);

  const handleSubmit = async () => {
    if (isSubmitting || !canRequestDocument) return;
    const trimmed = title.trim();
    if (!trimmed) {
      onAlert("요청 제목을 입력해 주세요.");
      return;
    }
    if (trimmed.length > TITLE_MAX_LENGTH) {
      onAlert(`요청 제목은 ${TITLE_MAX_LENGTH}자 이하로 입력해 주세요.`);
      return;
    }
    const trimmedDescription = normalizeNewlines(description).trim();
    if (utf8ByteLength(trimmedDescription) > DESCRIPTION_MAX_BYTES) {
      onAlert(`요청 설명은 ${DESCRIPTION_MAX_BYTES}byte 이하로 입력해 주세요.`);
      return;
    }
    setIsSubmitting(true);
    try {
      const result = await createPatentDesignDocumentRequest(detail.id, {
        title: trimmed,
        description: trimmedDescription || null,
      });
      if (result && typeof result === "object") {
        setTitle("");
        setDescription("");
        onAlert("추가 서류를 요청했습니다.");
        onReload();
      } else if (DOCUMENT_REQUEST_ERROR_MESSAGE[result] || ERROR_MESSAGE[result]) {
        onAlert(DOCUMENT_REQUEST_ERROR_MESSAGE[result] || ERROR_MESSAGE[result]);
      } else {
        onError(result);
      }
    } catch {
      onError();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = async (request) => {
    const action = CLOSE_ACTION[request.status];
    if (!action || closingId) return;
    if (!window.confirm(action.confirm(request.title))) return;
    setClosingId(request.id);
    try {
      const result = await closePatentDesignDocumentRequest(request.id);
      if (result && typeof result === "object") {
        onAlert("요청을 닫았습니다.");
        onReload();
      } else if (ERROR_MESSAGE[result]) {
        onAlert(ERROR_MESSAGE[result]);
      } else {
        onError(result);
      }
    } catch {
      onError();
    } finally {
      setClosingId(null);
    }
  };

  return (
    <DetailSection title="추가 서류 요청">
      <div style={pdStyles.form}>
        {!canRequestDocument && (
          <p style={pdStyles.hint}>취소·거절된 신청에는 추가 서류를 요청할 수 없습니다.</p>
        )}
        <div>
          <label htmlFor="patent-design-request-title" style={pdStyles.label}>
            요청 제목
          </label>
          <Input
            id="patent-design-request-title"
            maxLength={TITLE_MAX_LENGTH}
            placeholder="예) 서명한 위임장"
            value={title}
            disabled={!canRequestDocument}
            onChange={(e) => setTitle(e.target.value)}
          />
        </div>
        <div>
          <label htmlFor="patent-design-request-description" style={pdStyles.label}>
            요청 설명
          </label>
          <textarea
            id="patent-design-request-description"
            style={{ ...pdStyles.textarea, minHeight: "70px" }}
            value={description}
            disabled={!canRequestDocument}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>
        <div style={pdStyles.row}>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={isSubmitting || !canRequestDocument}
            style={disabledStyle(isSubmitting || !canRequestDocument)}
          >
            서류 요청
          </Button>
          <span style={pdStyles.hint}>요청하면 고객에게 메일이 발송돼요</span>
        </div>
      </div>

      {requests.length ? (
        <ul style={pdStyles.list}>
          {requests.map((request) => {
            const action = CLOSE_ACTION[request.status];
            return (
              <li key={request.id} style={pdStyles.card}>
                <div style={pdStyles.cardHeader}>
                  <div style={pdStyles.row}>
                    <strong>{request.title}</strong>
                    <Badge style={DOCUMENT_REQUEST_STATUS_STYLE[request.status]}>
                      {DOCUMENT_REQUEST_STATUS[request.status] || request.status}
                    </Badge>
                  </div>
                  {action && (
                    <Button
                      variant="secondary"
                      aria-label={`${request.title} ${action.label}`}
                      onClick={() => handleClose(request)}
                      disabled={closingId === request.id}
                      style={{ padding: "6px 12px", ...disabledStyle(closingId === request.id) }}
                    >
                      {action.label}
                    </Button>
                  )}
                </div>
                {request.description && <p style={pdStyles.content}>{request.description}</p>}
                <p style={pdStyles.muted}>
                  요청 {formatDateTime(request.created_at)}
                  {request.submitted_at && ` · 제출 ${formatDateTime(request.submitted_at)}`}
                </p>
                <FileList
                  files={toFileItems(request.files)}
                  disabled={isDownloading}
                  emptyText={
                    request.status === "requested" ? "아직 제출되지 않았어요" : "제출된 파일 없음"
                  }
                  onDownload={(file) => download([file], downloadPatentDesignDocument)}
                />
              </li>
            );
          })}
        </ul>
      ) : (
        <p style={pdStyles.muted}>요청한 서류가 없어요</p>
      )}
    </DetailSection>
  );
};

export default DocumentRequestSection;
